import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle } from "lucide-react";
import { useForm } from "@formspree/react";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export default function QuoteDrawer() {
  const { isOpen, service, closeDrawer } = useQuoteDrawer();
  const { toast } = useToast();
  const [state, handleSubmit] = useForm("mnnvbzke");
  const [formData, setFormData] = useState({
    projectType: service || "",
    budget: "",
    customPrice: "",
    timeline: "",
    name: "",
    email: "",
    phone: "",
    description: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (service) {
      setFormData(prev => ({ ...prev, projectType: service }));
    }
  }, [service]);

  useEffect(() => {
    const savedForm = localStorage.getItem('quoteFormData');
    if (savedForm) {
      setFormData(JSON.parse(savedForm));
    }
  }, []);

  useEffect(() => {
    if (Object.values(formData).some(val => val)) {
      localStorage.setItem('quoteFormData', JSON.stringify(formData));
    }
  }, [formData]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        document.getElementById('projectType')?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.projectType) newErrors.projectType = 'Project type is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });
    
    try {
      const response = await fetch('https://formspree.io/f/xovwnnew', {
        method: 'POST',
        body: formDataToSend,
        headers: { 'Accept': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.ok) {
        setShowSuccess(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (showSuccess) {
    return (
      <motion.div 
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        className="fixed inset-0 flex items-center justify-center bg-background z-50"
      >
        <div className="text-center p-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500 }}
            className="inline-block mb-4"
          >
            <CheckCircle className="h-16 w-16 text-green-500" />
          </motion.div>
          <h2 className="text-2xl font-bold mb-2">Request Submitted!</h2>
          <p className="text-muted-foreground mb-6">We'll get back to you soon</p>
          <Button 
          onClick={() => {
            setShowSuccess(false);
            closeDrawer();
          }}
          aria-label="Close success message"
        >
          Close
        </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div 
              className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={closeDrawer}
            />
            
            <div className="fixed inset-y-0 right-0 max-w-full flex">
              <div className="relative w-screen max-w-md">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6">
                    <div className="flex items-start justify-between">
                      <h2 className="text-lg font-medium text-gray-900">Get a Quote</h2>
                      <button 
                        type="button" 
                        className="text-gray-400 hover:text-gray-500"
                        onClick={closeDrawer}
                        aria-label="Close quote drawer"
                      >
                        <span className="sr-only">Close panel</span>
                        <X className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="mt-8">
                      <div className="flow-root">
                        <form onSubmit={onSubmit} className="space-y-6">
                          <div>
                            <Label htmlFor="projectType" className="block text-sm font-medium text-gray-700">
                              Project Type <span className="text-red-500">*</span>
                            </Label>
                            <Select 
                              value={formData.projectType}
                              onValueChange={(value) => handleInputChange("projectType", value)}
                            >
                              <SelectTrigger className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md">
                                <SelectValue placeholder="Select project type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="web-development">Web Development</SelectItem>
                                <SelectItem value="mobile-app">Mobile App</SelectItem>
                                <SelectItem value="cloud">Cloud Solutions</SelectItem>
                                <SelectItem value="design">UI/UX Design</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                            {errors.projectType && <p className="text-red-500">{errors.projectType}</p>}
                          </div>

                          <div>
                            <Label htmlFor="budget" className="block text-sm font-medium text-gray-700">
                              Budget Range <span className="text-red-500">*</span>
                            </Label>
                            <Select 
                              value={formData.budget}
                              onValueChange={(value) => handleInputChange("budget", value)}
                            >
                              <SelectTrigger className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md">
                                <SelectValue placeholder="Select budget" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="under-5k">Under $5,000</SelectItem>
                                <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                                <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                                <SelectItem value="custom">Custom Amount</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {formData.budget === 'custom' && (
                            <div>
                              <Label htmlFor="customPrice" className="block text-sm font-medium text-gray-700">
                                Custom Price <span className="text-red-500">*</span>
                              </Label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                                <Input
                                  id="customPrice"
                                  type="number"
                                  value={formData.customPrice}
                                  onChange={(e) => handleInputChange("customPrice", e.target.value)}
                                  placeholder="Enter amount"
                                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm pl-8"
                                />
                              </div>
                            </div>
                          )}

                          <div>
                            <Label htmlFor="timeline" className="block text-sm font-medium text-gray-700">
                              Timeline <span className="text-red-500">*</span>
                            </Label>
                            <Select 
                              value={formData.timeline}
                              onValueChange={(value) => handleInputChange("timeline", value)}
                            >
                              <SelectTrigger className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md">
                                <SelectValue placeholder="Select timeline" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1-3 months">1-3 months</SelectItem>
                                <SelectItem value="3-6 months">3-6 months</SelectItem>
                                <SelectItem value="6+ months">6+ months</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              Name <span className="text-red-500">*</span>
                            </Label>
                            <Input 
                              id="name" 
                              value={formData.name}
                              onChange={(e) => handleInputChange("name", e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                              placeholder="Your full name"
                              required 
                            />
                            {errors.name && <p className="text-red-500">{errors.name}</p>}
                          </div>

                          <div>
                            <Label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              Email <span className="text-red-500">*</span>
                            </Label>
                            <Input 
                              id="email" 
                              type="email" 
                              value={formData.email}
                              onChange={(e) => handleInputChange("email", e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                              placeholder="Your email address"
                              required 
                            />
                            {errors.email && <p className="text-red-500">{errors.email}</p>}
                          </div>

                          <div>
                            <Label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              Phone
                            </Label>
                            <Input 
                              id="phone" 
                              type="tel" 
                              value={formData.phone}
                              onChange={(e) => handleInputChange("phone", e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                              placeholder="Your phone number"
                            />
                          </div>

                          <div>
                            <Label htmlFor="description" className="block text-sm font-medium text-gray-700">
                              Project Details <span className="text-red-500">*</span>
                            </Label>
                            <Textarea 
                              id="description" 
                              value={formData.description}
                              onChange={(e) => handleInputChange("description", e.target.value)}
                              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                              placeholder="Describe your project requirements..."
                              maxLength={500}
                            />
                          </div>
                          <Button 
                            type="submit" 
                            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                            disabled={state.submitting || isLoading}
                          >
                            {isLoading ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : null}
                            Submit Quote Request
                          </Button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
